import {
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RingGeometry,
  SphereGeometry,
  Vector3
} from 'three';
import { teamCanSeeEntity } from '../../domain/entity/entityVision.js';
import { listAlliedMinionViewers } from './teamVisionViewers.js';
import { syncStructureRenderState } from './structureRenderState.js';
import { mapAllowsTrainingExtras } from '../controllers/mapSandboxExtras.js';

/**
 * entitySceneManager — Single Responsibility: Mapeo, renderizado y sincronización de proyectores 3D/placas HUD de entidades.
 *
 * Contrato público:
 *   createBuffAuraGroup(ringColor, sphereColor, ringRadiusInner, ringRadiusOuter, orbitRadius)
 *   updateEntityProjections(context)
 */

export function createBuffAuraGroup(ringColor, sphereColor, ringRadiusInner, ringRadiusOuter, orbitRadius) {
  const group = new Group();

  const ringMesh = new Mesh(
    new RingGeometry(ringRadiusInner, ringRadiusOuter, 32).rotateX(-Math.PI / 2),
    new MeshBasicMaterial({ color: ringColor, side: DoubleSide, transparent: true, opacity: 0.85 })
  );
  ringMesh.position.y = 0.05;
  group.add(ringMesh);

  const spheresGroup = new Group();
  const sphereGeo = new SphereGeometry(0.18, 12, 12);
  const sphereMat = new MeshStandardMaterial({
    color: sphereColor,
    emissive: sphereColor,
    emissiveIntensity: 0.8,
    roughness: 0.2
  });

  for (let i = 0; i < 3; i++) {
    const angle = (i * Math.PI * 2) / 3;
    const sphere = new Mesh(sphereGeo, sphereMat);
    sphere.position.set(Math.cos(angle) * orbitRadius, 0.35, Math.sin(angle) * orbitRadius);
    spheresGroup.add(sphere);
  }

  group.add(spheresGroup);
  group.visible = false;
  return { group, spheresGroup };
}

export function updateEntityProjections({
  championEntity, championGroup, playerPlate, respawnAsheBtn, camera,
  playerHpBar, hudHpBarInner, playerMpBar, hudMpBarInner, mpText, hudUI,
  redTurretEntity, garenEntity, bushZones, garenPlate, garenHpBar, garenGroup, garenStunBadge,
  dummyEntity, dummyPlateDiv, dummyHpBar, dummyGroup, turretRenderObjects,
  activeMinions, minionsUiContainer, scene, mapId = null,
  activeWards = [],
  wallBoxes = [],
  tempVec = new Vector3()
}) {
  if (championEntity.isDead) {
    championGroup.visible = false;
    playerPlate.style.display = 'none';
    respawnAsheBtn.style.display = 'inline-block';
  } else {
    championGroup.position.set(championEntity.x, championEntity.y, championEntity.z);
    championGroup.rotation.y = championEntity.facingAngle;

    tempVec.set(championEntity.x, 4.4, championEntity.z);
    tempVec.project(camera);
    playerPlate.style.left = `${(tempVec.x * 0.5 + 0.5) * 1200}px`;
    playerPlate.style.top = `${(-(tempVec.y * 0.5) + 0.5) * 800}px`;
    playerPlate.style.display = 'flex';
    championGroup.visible = true;

    const playerHpPct = Math.max(0, (championEntity.hp / championEntity.maxHp) * 100);
    playerHpBar.style.width = `${playerHpPct}%`;
    if (hudHpBarInner) hudHpBarInner.style.width = `${playerHpPct}%`;
    if (playerMpBar) {
      const playerMpPct = Math.max(0, (championEntity.mp / championEntity.maxMp) * 100);
      playerMpBar.style.width = `${playerMpPct}%`;
    }
    if (hudMpBarInner) {
      const playerMpPct = Math.max(0, (championEntity.mp / championEntity.maxMp) * 100);
      hudMpBarInner.style.width = `${playerMpPct}%`;
    }
    if (mpText) mpText.textContent = `${Math.floor(championEntity.mp)}/${championEntity.maxMp}`;
  }

  hudUI.updateChampionHUD(championEntity, redTurretEntity);

  projectTrainingExtras({
    showTraining: mapAllowsTrainingExtras(mapId),
    championEntity,
    bushZones,
    activeWards,
    wallBoxes,
    camera,
    tempVec,
    garenEntity,
    garenPlate,
    garenHpBar,
    garenGroup,
    garenStunBadge,
    dummyEntity,
    dummyPlateDiv,
    dummyHpBar,
    dummyGroup
  });

  turretRenderObjects.forEach((tr) => {
    if (syncStructureRenderState(tr) !== 'living') return;
    tempVec.set(tr.entity.x, 5.5, tr.entity.z);
    tempVec.project(camera);
    tr.plateDiv.style.left = `${(tempVec.x * 0.5 + 0.5) * 1200}px`;
    tr.plateDiv.style.top = `${(-(tempVec.y * 0.5) + 0.5) * 800}px`;
    tr.hpInner.style.width = `${Math.max(0, (tr.entity.hp / tr.entity.maxHp) * 100)}%`;
  });

  const alliedViewers = listAlliedMinionViewers(championEntity, activeMinions);
  for (let i = activeMinions.length - 1; i >= 0; i--) {
    const m = activeMinions[i];
    if (m.domain.isDead) {
      scene.remove(m.group);
      if (m.plateDiv.parentNode === minionsUiContainer) {
        minionsUiContainer.removeChild(m.plateDiv);
      }
      activeMinions.splice(i, 1);
    } else {
      m.group.position.set(m.domain.x, m.domain.y, m.domain.z);
      m.group.rotation.y = m.domain.facingAngle;

      const canSeeMinion = teamCanSeeEntity(
        championEntity.team,
        m.domain,
        alliedViewers,
        bushZones,
        activeWards,
        wallBoxes
      );
      m.group.visible = canSeeMinion;
      m.plateDiv.style.display = canSeeMinion ? 'flex' : 'none';

      tempVec.set(m.domain.x, 1.8, m.domain.z);
      tempVec.project(camera);
      m.plateDiv.style.left = `${(tempVec.x * 0.5 + 0.5) * 1200}px`;
      m.plateDiv.style.top = `${(-(tempVec.y * 0.5) + 0.5) * 800}px`;

      const hpPct = Math.max(0, (m.domain.hp / m.domain.maxHp) * 100);
      if (m.hpInner) m.hpInner.style.width = `${hpPct}%`;
    }
  }
}

function projectTrainingExtras({
  showTraining,
  championEntity,
  bushZones,
  activeWards = [],
  wallBoxes = [],
  camera,
  tempVec,
  garenEntity,
  garenPlate,
  garenHpBar,
  garenGroup,
  garenStunBadge,
  dummyEntity,
  dummyPlateDiv,
  dummyHpBar,
  dummyGroup
}) {
  if (!showTraining) {
    if (garenPlate) garenPlate.style.display = 'none';
    if (garenGroup) garenGroup.visible = false;
    if (dummyPlateDiv) dummyPlateDiv.style.display = 'none';
    if (dummyGroup) dummyGroup.visible = false;
    return;
  }
  const canSeeGaren = championEntity.canSeeEntity(garenEntity, bushZones, activeWards, wallBoxes);
  if (!garenEntity.isDead && canSeeGaren) {
    garenGroup.position.set(garenEntity.x, garenEntity.y || 0, garenEntity.z);
    tempVec.set(garenEntity.x, 4.4, garenEntity.z);
    tempVec.project(camera);
    garenPlate.style.left = `${(tempVec.x * 0.5 + 0.5) * 1200}px`;
    garenPlate.style.top = `${(-(tempVec.y * 0.5) + 0.5) * 800}px`;
    garenPlate.style.display = 'flex';
    garenGroup.visible = true;
    if (garenHpBar) {
      garenHpBar.style.width = `${Math.max(0, (garenEntity.hp / garenEntity.maxHp) * 100)}%`;
    }
    if (garenEntity.isStunned) {
      garenStunBadge.style.display = 'block';
      garenStunBadge.textContent = `¡ATURDIDO (${garenEntity.stunTimer.toFixed(1)}s)!`;
    } else {
      garenStunBadge.style.display = 'none';
    }
  } else {
    garenPlate.style.display = 'none';
    garenGroup.visible = false;
  }

  const canSeeDummy = championEntity.canSeeEntity(dummyEntity, bushZones, activeWards, wallBoxes);
  if (!dummyEntity.isDead && canSeeDummy) {
    dummyGroup.position.set(dummyEntity.x, dummyEntity.y || 0, dummyEntity.z);
    tempVec.set(dummyEntity.x, 4.2, dummyEntity.z);
    tempVec.project(camera);
    dummyPlateDiv.style.left = `${(tempVec.x * 0.5 + 0.5) * 1200}px`;
    dummyPlateDiv.style.top = `${(-(tempVec.y * 0.5) + 0.5) * 800}px`;
    dummyHpBar.style.width = `${Math.max(0, (dummyEntity.hp / dummyEntity.maxHp) * 100)}%`;
    dummyGroup.visible = true;
    dummyPlateDiv.style.display = 'flex';
  } else {
    dummyGroup.visible = false;
    dummyPlateDiv.style.display = 'none';
  }
}
